import { renewalDateStatuses } from '@/store/subscriptions/utils'
import {
  convertUTCToTSITimezoneDate,
  convertRFC3339ToMonthDayYearDateString
} from '../common/helper'
import type { Subscription } from '@/types/subscriptions'

export interface RenewalDate {
  status: string
  date: string
}

export interface DeviceSubscriptionFormattedDetails {
  activationDate: string
  subscriptionTier: string | undefined
  renewalDate: RenewalDate
  endDate: string
}

export const getDeviceSubscriptionIndex = (
  subscription: Subscription,
  deviceModel: string,
  deviceSerial: string
): number => {
  let deviceIndex = -1
  if (subscription) {
    const deviceIds = subscription.deviceIds
    if (deviceIds?.length) {
      deviceIds.forEach((ids, index) => {
        if (ids?.length) {
          ids.forEach((key) => {
            // Check if the key matches either the model-serial combination or just the serial
            if (key === `${deviceModel}-${deviceSerial}` || key === deviceSerial) {
              deviceIndex = index
            }
          })
        }
      })
    }
  }

  return deviceIndex
}

export const getDeviceSubscriptionTier = (
  getSKUMetadataTierNameFn: (id?: string) => string,
  subscription: Subscription
): string | undefined => {
  return getSKUMetadataTierNameFn(subscription?.id)
}

export const getDeviceSubscriptionActivationDate = (
  subscription: Subscription,
  deviceSubscriptionIndex: number
): string => {
  if (subscription && deviceSubscriptionIndex !== -1) {
    const activationDate = subscription.activationDate
      ? subscription.activationDate[deviceSubscriptionIndex]
      : ''
    if (activationDate) {
      return convertUTCToTSITimezoneDate(activationDate)
    }
  }

  return ''
}

export const getDeviceSubscriptionRenewalDate = (
  subscription: Subscription,
  deviceSubscriptionIndex: number
): RenewalDate => {
  const renewalDate: RenewalDate = {
    status: renewalDateStatuses.NotApplicable,
    date: ''
  }
  if (subscription && deviceSubscriptionIndex !== -1) {
    if (
      subscription.expires?.[deviceSubscriptionIndex] &&
      subscription.canceled?.[deviceSubscriptionIndex]
    ) {
      renewalDate.status = renewalDateStatuses.Canceled
    } else if (subscription.canceled?.[deviceSubscriptionIndex]) {
      renewalDate.status = renewalDateStatuses.CancellationPending
    } else if (subscription.billingInfo?.[deviceSubscriptionIndex]?.autoRenewalDate) {
      renewalDate.status = renewalDateStatuses.AutoRenewal
      renewalDate.date = convertRFC3339ToMonthDayYearDateString(
        subscription.billingInfo[deviceSubscriptionIndex].autoRenewalDate
      )
    }
  }

  return renewalDate
}

export const getDeviceSubscriptionEndDate = (
  subscription: Subscription,
  deviceSubscriptionIndex: number
): string => {
  if (subscription && deviceSubscriptionIndex !== -1) {
    if (subscription.expires?.[deviceSubscriptionIndex]) {
      return convertRFC3339ToMonthDayYearDateString(subscription.expires[deviceSubscriptionIndex])
    }
  }

  return ''
}

export const getDeviceSubscriptionFormattedDetails = (
  getSKUMetadataTierNameFn: (id?: string) => string,
  subscription: Subscription,
  model: string,
  serial: string
): DeviceSubscriptionFormattedDetails => {
  const deviceSubscriptionIndex = getDeviceSubscriptionIndex(subscription, model, serial)
  return {
    activationDate: getDeviceSubscriptionActivationDate(subscription, deviceSubscriptionIndex),
    subscriptionTier: getDeviceSubscriptionTier(getSKUMetadataTierNameFn, subscription),
    renewalDate: getDeviceSubscriptionRenewalDate(subscription, deviceSubscriptionIndex),
    endDate: getDeviceSubscriptionEndDate(subscription, deviceSubscriptionIndex)
  }
}
